// The playoff bracket and the pool below the line, available all season.
//
// Replaces PlayoffBracketDiagram and PlayoffBracket, which read a stored JSON
// bracket that only existed once an admin ran initializePlayoffs. This derives
// everything from the standings and weekly_standings, so it renders a
// projection during the regular season: the point of the tab is seeing where
// you would land if the playoffs started today.
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabase/supabase";
import { buildPlayoffPicture, PLAYOFF_WEEKS, ROUND_ONE_WEEK, SEMIS_WEEK, FINAL_WEEK } from "../../utils/playoffs";
import UserAvatar from "./UserAvatar";

const SEED_LABEL = { [ROUND_ONE_WEEK]: "Round one", [SEMIS_WEEK]: "Semi-finals", [FINAL_WEEK]: "Final" };

function Side({ member, points, isWinner, placeholder, decided }) {
  if (!member) {
    return (
      <div className="flex items-center gap-2 px-2.5 py-2" style={{ opacity: 0.6 }}>
        <div className="rounded-full flex-shrink-0" style={{ width: 26, height: 26, backgroundColor: "var(--color-bg-page)" }} />
        <span className="text-xs flex-1 min-w-0 truncate" style={{ color: "var(--color-text-muted)" }}>{placeholder}</span>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 px-2.5 py-2"
      style={{ backgroundColor: isWinner ? "var(--color-band-bg)" : "transparent" }}
    >
      <span
        className="flex-shrink-0 flex items-center justify-center rounded text-[10px] font-bold"
        style={{ width: 18, height: 18, backgroundColor: "var(--color-bg-page)", color: "var(--color-text-muted)" }}
      >
        {member.seed}
      </span>
      <UserAvatar member={member} size={24} />
      <span
        className="text-xs flex-1 min-w-0 truncate"
        style={{ color: "var(--color-text-strong)", fontWeight: isWinner ? 700 : 500 }}
      >
        {member.teamName || "Unnamed Team"}
      </span>
      <span
        className="text-xs font-bold flex-shrink-0"
        style={{ color: points === null ? "var(--color-text-faint)" : isWinner ? "var(--color-brand)" : "var(--color-text-muted)" }}
      >
        {points === null ? (decided ? "0" : "—") : points}
      </span>
    </div>
  );
}

function Match({ match, title, homePlaceholder, awayPlaceholder }) {
  const winnerId = match.winner?.id;
  return (
    <div className="mb-2">
      {title && (
        <div className="text-[10px] font-semibold uppercase tracking-wider mb-1 px-1" style={{ color: "var(--color-text-muted)" }}>
          {title}
        </div>
      )}
      <div
        className="rounded-xl border overflow-hidden divide-y"
        style={{ backgroundColor: "var(--color-bg-card)", borderColor: "var(--color-border)" }}
      >
        <Side
          member={match.home}
          points={match.homePoints}
          isWinner={Boolean(winnerId) && winnerId === match.home?.id}
          placeholder={homePlaceholder}
          decided={match.played}
        />
        <Side
          member={match.away}
          points={match.awayPoints}
          isWinner={Boolean(winnerId) && winnerId === match.away?.id}
          placeholder={awayPlaceholder}
          decided={match.played}
        />
      </div>
    </div>
  );
}

function Section({ week, children }) {
  return (
    <div className="mb-4">
      <div className="flex items-baseline gap-2 mb-1.5 px-1">
        <h4 className="text-sm font-bold" style={{ color: "var(--color-text-strong)" }}>{SEED_LABEL[week]}</h4>
        <span className="text-[11px]" style={{ color: "var(--color-text-muted)" }}>Week {week}</span>
      </div>
      {children}
    </div>
  );
}

export default function PlayoffPicture({ leagueId, standings }) {
  const [pointsByWeek, setPointsByWeek] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!leagueId) return;
      const { data } = await supabase
        .from("weekly_standings")
        .select("member_id, week, points")
        .eq("league_id", leagueId)
        .in("week", PLAYOFF_WEEKS);
      if (cancelled) return;

      // A week with no rows has not been scored, which is what leaves its
      // matches unresolved rather than showing them as 0-0 draws.
      const map = {};
      (data || []).forEach((row) => {
        if (!map[row.week]) map[row.week] = {};
        map[row.week][row.member_id] = row.points ?? 0;
      });
      setPointsByWeek(map);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [leagueId]);

  const picture = useMemo(
    () => buildPlayoffPicture(standings, pointsByWeek),
    [standings, pointsByWeek]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-4 border-hairline border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (picture.seeds.length === 0) {
    return (
      <div className="rounded-2xl p-10 border shadow-sm text-center" style={{ backgroundColor: "var(--color-bg-card)", borderColor: "var(--color-border)" }}>
        <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>
          No managers have joined this league yet.
        </p>
      </div>
    );
  }

  const { bracket, pool, projected } = picture;

  return (
    <div>
      {/* A caption, not a notice. Without it the bracket reads as decided,
          which matters most before any football has been played: every manager
          is on zero, so the seeds come from the tiebreak rather than from
          anything anyone has done. The second sentence it used to carry, that
          seeds move with the standings, said the same thing twice. */}
      {projected && (
        <div className="text-xs mb-3" style={{ color: "var(--color-text-muted)" }}>
          If the playoffs started today.
        </div>
      )}

      <Section week={ROUND_ONE_WEEK}>
        <div
          className="rounded-xl border px-2.5 py-2 mb-2 flex items-center gap-2 flex-wrap"
          style={{ backgroundColor: "var(--color-bg-row-alt)", borderColor: "var(--color-border)" }}
        >
          <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--color-text-muted)" }}>
            Bye
          </span>
          {bracket.byes.map((member) => (
            <span key={member.id} className="flex items-center gap-1.5">
              <span className="text-[10px] font-bold" style={{ color: "var(--color-text-muted)" }}>{member.seed}</span>
              <span className="text-xs font-semibold truncate" style={{ color: "var(--color-text-strong)" }}>
                {member.teamName || "Unnamed Team"}
              </span>
            </span>
          ))}
        </div>
        {bracket.roundOne.map((match, i) => (
          <Match key={i} match={match} />
        ))}
      </Section>

      <Section week={SEMIS_WEEK}>
        {bracket.semis.map((match, i) => (
          <Match
            key={i}
            match={match}
            awayPlaceholder={`Winner of round one`}
          />
        ))}
      </Section>

      <Section week={FINAL_WEEK}>
        <Match
          match={bracket.final}
          homePlaceholder="Winner of semi-final"
          awayPlaceholder="Winner of semi-final"
        />
        <div className="text-[10px] font-semibold uppercase tracking-wider mb-1 mt-3 px-1" style={{ color: "var(--color-text-muted)" }}>
          Third place
        </div>
        <Match
          match={bracket.thirdPlace}
          homePlaceholder="Beaten semi-finalist"
          awayPlaceholder="Beaten semi-finalist"
        />
      </Section>

      {pool.participants.length > 0 && (
        <div className="mb-4">
          <div className="flex items-baseline gap-2 mb-1 px-1">
            <h4 className="text-sm font-bold" style={{ color: "var(--color-text-strong)" }}>First pick race</h4>
            <span className="text-[11px]" style={{ color: "var(--color-text-muted)" }}>
              Weeks {ROUND_ONE_WEEK} to {FINAL_WEEK}
            </span>
          </div>
          <p className="text-[11px] mb-2 px-1" style={{ color: "var(--color-text-muted)" }}>
            Everyone below the playoff line. No bracket: highest total across the three weeks takes
            first pick in next season's draft.
          </p>
          <div
            className="rounded-xl border overflow-hidden"
            style={{ backgroundColor: "var(--color-bg-card)", borderColor: "var(--color-border)" }}
          >
            {pool.participants.map((member, idx) => (
              <div
                key={member.id}
                className="flex items-center gap-2.5 px-3 py-2.5"
                style={{ backgroundColor: idx % 2 === 1 ? "var(--color-bg-row-alt)" : "var(--color-bg-card)" }}
              >
                <span
                  className="flex-shrink-0 flex items-center justify-center rounded text-[10px] font-bold"
                  style={{ width: 18, height: 18, backgroundColor: "var(--color-bg-page)", color: "var(--color-text-muted)" }}
                >
                  {idx + 1}
                </span>
                <UserAvatar member={member} size={26} />
                <span className="text-xs flex-1 min-w-0 truncate" style={{ color: "var(--color-text-strong)" }}>
                  {member.teamName || "Unnamed Team"}
                </span>
                {idx === 0 && pool.decided && (
                  <span
                    className="text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: "var(--color-success-bg)", color: "var(--color-success-text)" }}
                  >
                    1ST PICK
                  </span>
                )}
                <span className="text-xs font-bold flex-shrink-0" style={{ color: "var(--color-brand)" }}>
                  {member.total}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
